import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { vehicleRegistrationAPI } from '../services/api';
import ExcelJS from 'exceljs';
import SearchableMultiSelect from './SearchableMultiSelect';
import Swal from 'sweetalert2';
import './SpreadsheetReport.css';

const SpreadsheetReport = ({ user }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedUnits, setSelectedUnits] = useState(['all']); // Thay đổi thành array
  const [currentPage, setCurrentPage] = useState(1);
  const [allRecords, setAllRecords] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const RECORDS_PER_PAGE = 1000;

  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [dragOverItemIndex, setDragOverItemIndex] = useState(null);
  const containerRef = React.useRef(null);

  // Fetch data lần đầu khi component mount
  useEffect(() => {
    fetchReportData(1, true);
  }, []);

  const handleDragStart = (e, index) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverItemIndex(index);
  };

  const handleDragOverContainer = (e) => {
    if (!containerRef.current || draggedItemIndex === null) return;
    
    const container = containerRef.current;
    const { top, bottom } = container.getBoundingClientRect();
    const threshold = 60; // Khoảng cách từ viền (pixel) để bắt đầu cuộn
    const scrollSpeed = 15; // Tốc độ cuộn
    
    if (e.clientY < top + threshold) {
      container.scrollTop -= scrollSpeed;
    } else if (e.clientY > bottom - threshold) {
      container.scrollTop += scrollSpeed;
    }
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedItemIndex === null || draggedItemIndex === dropIndex) {
      setDraggedItemIndex(null);
      setDragOverItemIndex(null);
      return;
    }

    const newTableData = [...tableStructure.tableData];
    const draggedItem = newTableData[draggedItemIndex];
    newTableData.splice(draggedItemIndex, 1);
    newTableData.splice(dropIndex, 0, draggedItem);

    const unitIds = newTableData.map(row => row.unit.id);

    try {
      await vehicleRegistrationAPI.reorderUnits(unitIds);
      fetchReportData(1, true);
    } catch (error) {
      console.error('Error reordering units:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi',
        text: 'Không thể thay đổi thứ tự địa điểm'
      });
    }

    setDraggedItemIndex(null);
    setDragOverItemIndex(null);
  };

  // Auto-select unit for supervisor if they only have access to one unit
  // Tránh vòng lặp tải: chỉ set khi khác với lựa chọn hiện tại
  useEffect(() => {
    if (user.role === 'supervisor' && reportData?.units && reportData.units.length === 1) {
      const onlyId = reportData.units[0].id.toString();
      const isAlreadySelected = selectedUnits.length === 1 && selectedUnits[0] === onlyId;
      if (!isAlreadySelected) {
        setSelectedUnits([onlyId]);
      }
    }
  }, [user.role, reportData?.units, selectedUnits]);

  const fetchReportData = async (page = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const params = {
        startDate: startDate,
        endDate: endDate,
        page,
        limit: RECORDS_PER_PAGE
      };

      // Nếu user là unit, chỉ lấy dữ liệu của địa điểm họ
      if (user.role === 'unit' && user.unitId) {
        params.unitId = user.unitId;
      }

      // Nếu user là supervisor và đã chọn đơn vị cụ thể (không phải 'all')
      if (user.role === 'supervisor' && !selectedUnits.includes('all')) {
        params.unitIds = selectedUnits; // Gửi array các unitId
      }

      const response = await vehicleRegistrationAPI.getReportData(params);
      const data = response.data;
      
      if (reset) {
        setAllRecords(data.records);
        setReportData({
          ...data,
          records: data.records
        });
      } else {
        const updatedRecords = [...allRecords, ...data.records];
        setAllRecords(updatedRecords);
        setReportData({
          ...data,
          records: updatedRecords
        });
      }
      
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  const loadMoreRecords = () => {
    if (reportData?.pagination && currentPage < reportData.pagination.totalPages) {
      fetchReportData(currentPage + 1, false);
    }
  };

  // Hàm fetch toàn bộ dữ liệu cho export Excel (fetch theo batch)
  const fetchAllRecordsForExport = async () => {
    try {
      let allRecordsData = [];
      let currentPage = 1;
      let totalPages = 1;
      const batchSize = 5000; // Fetch 5000 records mỗi lần
      
      // Fetch trang đầu tiên để biết tổng số trang
      const params = {
        startDate: startDate,
        endDate: endDate,
        page: currentPage,
        limit: batchSize
      };

      // Nếu user là unit, chỉ lấy dữ liệu của địa điểm họ
      if (user.role === 'unit' && user.unitId) {
        params.unitId = user.unitId;
      }

      // Nếu user là supervisor và đã chọn đơn vị cụ thể (không phải 'all')
      if (user.role === 'supervisor' && !selectedUnits.includes('all')) {
        params.unitIds = selectedUnits;
      }

      const firstResponse = await vehicleRegistrationAPI.getReportData(params);
      const firstData = firstResponse.data;
      
      allRecordsData = [...firstData.records];
      totalPages = firstData.pagination?.totalPages || 1;
      
      // Fetch các trang còn lại
      for (let page = 2; page <= totalPages; page++) {
        params.page = page;
        const response = await vehicleRegistrationAPI.getReportData(params);
        allRecordsData = [...allRecordsData, ...response.data.records];
      }
      
      // Trả về data với toàn bộ records
      return {
        ...firstData,
        records: allRecordsData
      };
    } catch (error) {
      console.error('Error fetching all records for export:', error);
      throw error;
    }
  };

  const handleUnitsChange = (newSelectedUnits) => {
    const normalized = (newSelectedUnits || []).map(id => id.toString());
    // Nếu chọn 'all', xóa tất cả các địa điểm khác
    if (normalized.includes('all')) {
      setSelectedUnits(['all']);
      return;
    }

    // Nếu xoá hết thì tự quay về 'all'
    const cleaned = normalized.filter(id => id !== 'all');
    if (cleaned.length === 0) {
      setSelectedUnits(['all']);
      return;
    }

    setSelectedUnits(cleaned);
  };

  // Hàm xử lý khi bấm nút tìm kiếm
  const handleSearch = () => {
    setCurrentPage(1);
    setAllRecords([]);
    fetchReportData(1, true);
  };

  // Hàm tạo dữ liệu tổng hợp (như cũ)
  const buildAggregatedTableData = (filteredUnits, vehicleTypes, businessTypes, records, unitTargets = null) => {
    // Tạo map để tra cứu nhanh và tổng hợp dữ liệu từ nhiều ngày
    const recordsMap = {};
    records.forEach(record => {
      const key = `${record.unitId}-${record.businessTypeId}-${record.vehicleTypeId}`;
      // Nếu đã có dữ liệu cho key này, cộng thêm vào
      if (recordsMap[key]) {
        recordsMap[key] += record.quantity;
      } else {
        recordsMap[key] = record.quantity;
      }
    });

    // Tạo cấu trúc cột
    const columns = [];
    vehicleTypes.forEach(vehicleType => {
      const vehicleBusinessTypes = businessTypes.filter(bt => 
        bt.vehicleTypes.some(vt => vt.id === vehicleType.id)
      );
      
      vehicleBusinessTypes.forEach(businessType => {
        columns.push({
          vehicleType,
          businessType,
          key: `${vehicleType.id}-${businessType.id}`
        });
      });
    });

    // Tạo dữ liệu cho từng dòng
    const tableData = filteredUnits.map(unit => {
      const rowData = { unit };
      let hasData = false; // Kiểm tra đơn vị có dữ liệu không

      columns.forEach(col => {
        const key = `${unit.id}-${col.businessType.id}-${col.vehicleType.id}`;
        const value = recordsMap[key] || 0;
        rowData[col.key] = value;

        // Nếu có ít nhất 1 giá trị > 0 thì đơn vị có dữ liệu
        if (value > 0) {
          hasData = true;
        }
      });

      // Thêm flag để đánh dấu đơn vị chưa có dữ liệu
      rowData.hasData = hasData;
      return rowData;
    });

    // Tính tổng cho mỗi cột
    const totals = {};
    columns.forEach(col => {
      totals[col.key] = tableData.reduce((sum, row) => sum + (row[col.key] || 0), 0);
    });

    return { columns, tableData, totals, isDaily: false };
  };

  // Hàm tạo dữ liệu từng ngày cho nhiều địa điểm
  const buildDailyTableData = (filteredUnits, vehicleTypes, businessTypes, records) => {
    // Lọc records theo các đơn vị đã chọn
    const unitRecords = records.filter(record => 
      filteredUnits.some(unit => unit.id === record.unitId)
    );

    // Tạo danh sách tất cả các ngày trong khoảng thời gian
    const allDates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      allDates.push(new Date(date).toISOString().split('T')[0]);
    }

    // Nhóm records theo ngày
    const recordsByDate = {};
    unitRecords.forEach(record => {
      const date = record.recordDate;
      if (!recordsByDate[date]) {
        recordsByDate[date] = [];
      }
      recordsByDate[date].push(record);
    });

    // Tạo cấu trúc cột
    const columns = [];
    vehicleTypes.forEach(vehicleType => {
      const vehicleBusinessTypes = businessTypes.filter(bt => 
        bt.vehicleTypes.some(vt => vt.id === vehicleType.id)
      );
      
      vehicleBusinessTypes.forEach(businessType => {
        columns.push({
          vehicleType,
          businessType,
          key: `${vehicleType.id}-${businessType.id}`
        });
      });
    });

    // Tạo dữ liệu cho từng ngày (kể cả ngày không có dữ liệu)
    const tableData = allDates.map(date => {
      const dayRecords = recordsByDate[date] || [];
      const rowData = { 
        date: new Date(date).toLocaleDateString('vi-VN'),
        originalDate: date,
        units: filteredUnits // Lưu danh sách các đơn vị
      };

      let hasData = false;

      columns.forEach(col => {
        const value = dayRecords
          .filter(r => r.businessTypeId === col.businessType.id && r.vehicleTypeId === col.vehicleType.id)
          .reduce((sum, r) => sum + (r.quantity || 0), 0);
        rowData[col.key] = value;

        if (value > 0) {
          hasData = true;
        }
      });

      rowData.hasData = hasData;
      return rowData;
    });

    // Tính tổng cho mỗi cột
    const totals = {};
    columns.forEach(col => {
      totals[col.key] = tableData.reduce((sum, row) => sum + (row[col.key] || 0), 0);
    });

    return { columns, tableData, totals, isDaily: true };
  };

  // Hàm tạo dữ liệu từng ngày và tách theo từng địa điểm (khi chọn nhiều địa điểm)
  const buildDailyPerUnitTableData = (filteredUnits, vehicleTypes, businessTypes, records) => {
    // Tạo danh sách tất cả các ngày trong khoảng thời gian
    const allDates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);

    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      allDates.push(new Date(date).toISOString().split('T')[0]);
    }

    // Tạo cấu trúc cột
    const columns = [];
    vehicleTypes.forEach(vehicleType => {
      const vehicleBusinessTypes = businessTypes.filter(bt =>
        bt.vehicleTypes.some(vt => vt.id === vehicleType.id)
      );
      vehicleBusinessTypes.forEach(businessType => {
        columns.push({
          vehicleType,
          businessType,
          key: `${vehicleType.id}-${businessType.id}`
        });
      });
    });

    // Tạo dữ liệu: nhóm theo từng đơn vị, dưới mỗi đơn vị là các ngày
    const tableData = [];
    filteredUnits.forEach(unit => {
      // Dòng tiêu đề nhóm đơn vị
      tableData.push({ isGroupHeader: true, unit });

      allDates.forEach(date => {
        const dayUnitRecords = records.filter(r => r.recordDate === date && r.unitId === unit.id);
        const rowData = {
          date: new Date(date).toLocaleDateString('vi-VN'),
          originalDate: date,
          unit
        };

        let hasData = false;
        columns.forEach(col => {
          const value = dayUnitRecords
            .filter(r => r.businessTypeId === col.businessType.id && r.vehicleTypeId === col.vehicleType.id)
            .reduce((sum, r) => sum + (r.quantity || 0), 0);
          rowData[col.key] = value;
          if (value > 0) hasData = true;
        });

        rowData.hasData = hasData;
        tableData.push(rowData);
      });
    });

    // Tính tổng cho mỗi cột
    const totals = {};
    columns.forEach(col => {
      totals[col.key] = tableData
        .filter(row => !row.isGroupHeader)
        .reduce((sum, row) => sum + (row[col.key] || 0), 0);
    });

    return { columns, tableData, totals, isDaily: true, isDailyPerUnit: true };
  };

  // Tạo cấu trúc dữ liệu cho bảng với memoization
  const tableStructure = useMemo(() => {
    if (!reportData) return null;

    const { units, vehicleTypes, businessTypes, records } = reportData;

    // Lọc units theo selectedUnits
    let filteredUnits;
    if (selectedUnits.includes('all')) {
      filteredUnits = units;
    } else {
      filteredUnits = units.filter(unit => selectedUnits.includes(unit.id.toString()));
    }

    // Nếu chọn địa điểm cụ thể (không phải 'all') và khoảng thời gian > 1 ngày
    const isSingleDay = startDate === endDate;
    if (!selectedUnits.includes('all') && !isSingleDay) {
      if (filteredUnits.length > 1) {
        // Chọn nhiều địa điểm: tách theo từng địa điểm từng ngày
        return buildDailyPerUnitTableData(filteredUnits, vehicleTypes, businessTypes, records);
      }
      // Chọn 1 địa điểm: hiển thị theo từng ngày
      return buildDailyTableData(filteredUnits, vehicleTypes, businessTypes, records);
    }

    // Nếu chọn "Tất cả địa điểm", hiển thị dữ liệu tổng hợp (như cũ)
    return buildAggregatedTableData(filteredUnits, vehicleTypes, businessTypes, records);
  }, [reportData, selectedUnits, startDate, endDate]);

  // Memoize grouped columns để tránh tính toán lại
  const groupedColumns = useMemo(() => {
    if (!tableStructure) return {};
    
    const { columns } = tableStructure;
    const grouped = {};
    columns.forEach(col => {
      if (!grouped[col.vehicleType.id]) {
        grouped[col.vehicleType.id] = {
          vehicleType: col.vehicleType,
          columns: []
        };
      }
      grouped[col.vehicleType.id].columns.push(col);
    });
    return grouped;
  }, [tableStructure]);

  // Tạo options cho SearchableMultiSelect
  const unitOptions = useMemo(() => {
    if (!reportData?.units) return [];

    let options = [];
    
    // Với supervisor, chỉ hiển thị "Tất cả địa điểm" nếu có nhiều hơn 1 đơn vị được phân quyền
    if (user.role === 'supervisor' && reportData.units.length > 1) {
      options.push({ id: 'all', name: 'Tất cả địa điểm' });
    } else if (user.role === 'supervisor' && reportData.units.length === 1) {
      // Nếu supervisor chỉ có quyền xem 1 đơn vị, không hiển thị "Tất cả"
    } else {
      // Admin thì hiển thị bình thường
      options.push({ id: 'all', name: 'Tất cả địa điểm' });
    }

    // Thêm các đơn vị cụ thể, chuẩn hóa id thành string để so sánh ổn định
    options = [
      ...options,
      ...reportData.units.map(u => ({ id: String(u.id), name: u.name }))
    ];

    return options;
  }, [reportData?.units, user.role]);

  if (loading) {
    return <div className="loading">Đang tải dữ liệu...</div>;
  }

  if (!tableStructure) {
    return <div className="no-data">Không có dữ liệu</div>;
  }

  const { columns, tableData, totals, isDaily, isDailyPerUnit } = tableStructure;

  // Sử dụng groupedColumns đã được memoize

  const formatDateRange = () => {
    if (startDate === endDate) {
      return `ngày ${new Date(startDate).toLocaleDateString('vi-VN')}`;
    }
    return `từ ${new Date(startDate).toLocaleDateString('vi-VN')} đến ${new Date(endDate).toLocaleDateString('vi-VN')}`;
  };

  // Hàm xuất Excel
  const exportToExcel = async () => {
    if (!tableStructure) {
      Swal.fire({
        icon: 'warning',
        title: 'Không có dữ liệu',
        text: 'Không có dữ liệu để xuất',
        confirmButtonColor: '#1e3a8a'
      });
      return;
    }

    try {
      // Hiển thị loading
      const exportBtn = document.querySelector('.export-excel-btn');
      if (exportBtn) {
        exportBtn.disabled = true;
        exportBtn.innerHTML = '⏳';
      }

      // Fetch toàn bộ dữ liệu cho export
      const fullData = await fetchAllRecordsForExport();
      const { vehicleTypes, businessTypes, records: allRecords, unitTargets } = fullData;

      // Lọc units theo selectedUnits
      let filteredUnits;
      if (selectedUnits.includes('all')) {
        filteredUnits = fullData.units;
      } else {
        filteredUnits = fullData.units.filter(unit => selectedUnits.includes(unit.id.toString()));
      }

      // Tạo lại table structure với toàn bộ dữ liệu
      const isSingleDay = startDate === endDate;
      let fullTableStructure;
      
      if (!selectedUnits.includes('all') && !isSingleDay) {
        if (filteredUnits.length > 1) {
          fullTableStructure = buildDailyPerUnitTableData(filteredUnits, vehicleTypes, businessTypes, allRecords);
        } else {
          fullTableStructure = buildDailyTableData(filteredUnits, vehicleTypes, businessTypes, allRecords);
        }
      } else {
        fullTableStructure = buildAggregatedTableData(filteredUnits, vehicleTypes, businessTypes, allRecords, unitTargets);
      }

      const { columns, tableData, totals, isDaily, isDailyPerUnit } = fullTableStructure;

      const filteredRecords = selectedUnits.includes('all')
        ? allRecords
        : allRecords.filter(record => selectedUnits.includes(record.unitId.toString()));
      const ineligibleBusinessType = businessTypes.find(bt => bt.code === 'KH_DU_DK_DVC');
      
      // Tạo workbook với ExcelJS
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Báo cáo tổng hợp');

      // Thiết lập độ rộng cột
      const columnWidths = [
        { width: 30 }, // Cột địa điểm
        { width: 25 }, // Mô tô - Tổng số hồ sơ đăng ký mới
        { width: 25 }, // Mô tô - Số hồ sơ đủ điều kiện
        { width: 30 }, // Mô tô - Số hồ sơ đã đăng ký toàn trình
        { width: 35 }, // Mô tô - Số hồ sơ không đủ điều kiện
        { width: 15 }, // Mô tô - Tỷ lệ %
        { width: 25 }, // Ô tô - Tổng số hồ sơ đăng ký mới
        { width: 25 }, // Ô tô - Số hồ sơ đủ điều kiện
        { width: 30 }, // Ô tô - Số hồ sơ đã đăng ký toàn trình
        { width: 35 }, // Ô tô - Số hồ sơ không đủ điều kiện
        { width: 15 }, // Ô tô - Tỷ lệ %
      ];
      
      // Thêm độ rộng cho các cột động
      columns.forEach(() => {
        columnWidths.push({ width: 20 });
      });
      
      worksheet.columns = columnWidths;

      let currentRow = 1;

      // Tạo grouped columns cho export
      const exportGroupedColumns = {};
      columns.forEach(col => {
        if (!exportGroupedColumns[col.vehicleType.id]) {
          exportGroupedColumns[col.vehicleType.id] = {
            vehicleType: col.vehicleType,
            columns: []
          };
        }
        exportGroupedColumns[col.vehicleType.id].columns.push(col);
      });

    // Tiêu đề báo cáo
    const titleCell = worksheet.getCell(currentRow, 1);
    titleCell.value = `BÁO CÁO TỔNG HỢP ${formatDateRange().toUpperCase()}`;
    
    // Tính tổng số cột thực tế
    let totalActualColumns = 11; // 1 cột địa điểm + 10 cột thống kê (5 mô tô + 5 ô tô)
    totalActualColumns += columns.length;
    
    worksheet.mergeCells(currentRow, 1, currentRow, totalActualColumns);
    Object.assign(titleCell, {
      font: { name: 'Arial', size: 18, bold: true, color: { argb: 'FFFFFFFF' } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
      border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }
    });
    worksheet.getRow(currentRow).height = 45;
    currentRow += 2;

    // Header cấp 1
    const vehicleHeaderRow = worksheet.getRow(currentRow);
    vehicleHeaderRow.getCell(1).value = isDaily ? (isDailyPerUnit ? 'Ngày - Địa điểm' : 'Ngày') : 'Địa điểm';
    const vehicleColors = { 'Mô tô': 'FF3B82F6', 'Ô tô': 'FF059669', 'Xe chuyên dụng': 'FFB91C1C' };
    let colIndex = 2;

    ['Mô tô', 'Ô tô'].forEach(vehicleGroup => {
      const startCol = colIndex;
      vehicleHeaderRow.getCell(startCol).value = vehicleGroup;
      worksheet.mergeCells(currentRow, startCol, currentRow, startCol + 4);
      for (let col = startCol; col <= startCol + 4; col++) {
        Object.assign(vehicleHeaderRow.getCell(col), {
          font: { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: vehicleColors[vehicleGroup] } },
          alignment: { horizontal: 'center', vertical: 'middle' },
          border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }
        });
      }
      colIndex += 5;
    });

    Object.values(exportGroupedColumns).forEach(group => {
      const startCol = colIndex;
      vehicleHeaderRow.getCell(startCol).value = group.vehicleType.name;
      
      const actualColumnCount = group.columns.length;
      
      worksheet.mergeCells(currentRow, startCol, currentRow, startCol + actualColumnCount - 1);
      const color = vehicleColors[group.vehicleType.category] || 'FF888888';
      for (let col = startCol; col < startCol + actualColumnCount; col++) {
        Object.assign(vehicleHeaderRow.getCell(col), {
          font: { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: color } },
          alignment: { horizontal: 'center', vertical: 'middle' },
          border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }
        });
      }
      colIndex += actualColumnCount;
    });
    vehicleHeaderRow.height = 35;
    currentRow++;

    // Header cấp 2
    const businessHeaderRow = worksheet.getRow(currentRow);
    businessHeaderRow.getCell(1).value = 'STT';
    let businessColIndex = 2;
    const statsHeaders = [
      'Tổng số hồ sơ đăng ký mới',
      'Số hồ sơ đủ điều kiện',
      'Số hồ sơ đã đăng ký toàn trình',
      'Số hồ sơ không đủ điều kiện đăng ký dịch vụ công',
      'Tỷ lệ (%)'
    ];
    [...statsHeaders, ...statsHeaders].forEach(header => {
      businessHeaderRow.getCell(businessColIndex++).value = header;
    });
    Object.values(exportGroupedColumns).forEach(group => {
      group.columns.forEach(col => {
        businessHeaderRow.getCell(businessColIndex++).value = col.businessType.name;
      });
    });
    businessHeaderRow.eachCell(cell => {
      Object.assign(cell, {
        font: { name: 'Arial', size: 12, bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF12509C' } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }
      });
    });
    businessHeaderRow.height = 80;
    currentRow++;

    // Tìm business type "Dịch vụ công toàn trình"
    const dichVuCongBusinessType = businessTypes.find(bt => bt.code === 'DICH_VU_CONG');

    // Hàm tính thống kê
    const calculateStats = (recordsToProcess, category) => {
      const categoryVehicleTypes = vehicleTypes.filter(vt => vt.category === category);
      let total = 0, eligible = 0, ineligible = 0, dichVuCong = 0;
      recordsToProcess.forEach(record => {
        if (categoryVehicleTypes.some(vt => vt.id === record.vehicleTypeId)) {
          total += record.quantity;
          if (ineligibleBusinessType && record.businessTypeId === ineligibleBusinessType.id) {
            ineligible += record.quantity;
          } else if (dichVuCongBusinessType && record.businessTypeId === dichVuCongBusinessType.id) {
            dichVuCong += record.quantity;
            eligible += record.quantity; // Dịch vụ công cũng được tính vào đủ điều kiện
          } else {
            eligible += record.quantity;
          }
        }
      });
      return { total, eligible, ineligible, dichVuCong };
    };

    // Dòng tổng
    const totalRow = worksheet.getRow(currentRow);
    totalRow.getCell(1).value = 'Tổng';
    let totalColIndex = 2;
    ['moto', 'oto'].forEach(category => {
      const stats = calculateStats(filteredRecords, category);
      totalRow.getCell(totalColIndex).value = stats.total;
      totalRow.getCell(totalColIndex + 1).value = stats.eligible;
      totalRow.getCell(totalColIndex + 2).value = stats.dichVuCong; // Dữ liệu từ cột Dịch vụ công toàn trình
      totalRow.getCell(totalColIndex + 3).value = stats.ineligible;
      const ratioCell = totalRow.getCell(totalColIndex + 4);
      ratioCell.value = { formula: `IF(${totalRow.getCell(totalColIndex).address}>0, ${totalRow.getCell(totalColIndex + 1).address}/${totalRow.getCell(totalColIndex).address}, 0)` };
      ratioCell.numFmt = '0.00%';
      totalColIndex += 5;
    });
    columns.forEach(col => {
      const totalValue = totals[col.key];
      totalRow.getCell(totalColIndex++).value = totalValue || 0;
    });
    totalRow.eachCell(cell => {
      Object.assign(cell, {
        font: { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }
      });
    });
    totalRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
    totalRow.height = 40;
    currentRow++;

    // Dữ liệu từng đơn vị hoặc từng ngày
    if (isDaily) {
      // Hiển thị dữ liệu từng ngày
      tableData.forEach((row, index) => {
        // Dòng tiêu đề nhóm theo đơn vị (khi tách theo từng đơn vị)
        if (isDailyPerUnit && row.isGroupHeader) {
          const groupRow = worksheet.getRow(currentRow);
          groupRow.getCell(1).value = row.unit.name;
          worksheet.mergeCells(currentRow, 1, currentRow, totalActualColumns);
          for (let i = 1; i <= totalActualColumns; i++) {
            const cell = groupRow.getCell(i);
            Object.assign(cell, {
              font: { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFFFFFF' } },
              fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0EA5E9' } },
              alignment: { horizontal: 'left', vertical: 'middle' },
              border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }
            });
          }
          groupRow.height = 30;
          currentRow++;
          return; // sang hàng tiếp theo
        }

        const dataRow = worksheet.getRow(currentRow);
        dataRow.getCell(1).value = isDailyPerUnit ? row.date : row.date;
        let dataColIndex = 2;

        let dayRecords = [];
        if (isDailyPerUnit) {
           dayRecords = filteredRecords.filter(r => r.recordDate === row.originalDate && r.unitId === row.unit.id);
        } else {
           dayRecords = filteredRecords.filter(r => r.recordDate === row.originalDate && (row.units || []).some(u => u.id === r.unitId));
        }

        ['moto', 'oto'].forEach(category => {
          const stats = calculateStats(dayRecords, category);
          dataRow.getCell(dataColIndex).value = stats.total;
          dataRow.getCell(dataColIndex + 1).value = stats.eligible;
          dataRow.getCell(dataColIndex + 2).value = stats.dichVuCong;
          dataRow.getCell(dataColIndex + 3).value = stats.ineligible;
          const ratioCell = dataRow.getCell(dataColIndex + 4);
          ratioCell.value = { formula: `IF(${dataRow.getCell(dataColIndex).address}>0, ${dataRow.getCell(dataColIndex + 1).address}/${dataRow.getCell(dataColIndex).address}, 0)` };
          ratioCell.numFmt = '0.00%';
          dataColIndex += 5;
        });

        columns.forEach(col => {
          const cellValue = row[col.key];
          dataRow.getCell(dataColIndex++).value = cellValue || 0;
        });

        dataRow.eachCell((cell, colNumber) => {
          cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
          if (colNumber === 1) {
            Object.assign(cell, {
              font: { name: 'Arial', size: 14, bold: true },
              alignment: { horizontal: 'center', vertical: 'middle' },
              fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
            });
          } else {
            cell.font = { name: 'Arial', size: 14 };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            if (index % 2 === 0) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
            } else {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
            }
            if (!row.hasData) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
              cell.font = { name: 'Arial', size: 14, color: { argb: 'FF999999' } };
            } else if (cell.value > 0 && typeof cell.value === 'number') {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E8' } };
              cell.font = { name: 'Arial', size: 14, color: { argb: 'FF2D5A2D' } };
            }
          }
        });
        dataRow.height = 38;
        currentRow++;
      });
    } else {
      // Hiển thị dữ liệu tổng hợp từng đơn vị (như cũ)
      tableData.forEach((row, index) => {
        const dataRow = worksheet.getRow(currentRow);
        dataRow.getCell(1).value = row.unit.name;
        let dataColIndex = 2;
        const unitRecords = filteredRecords.filter(r => r.unitId === row.unit.id);

        ['moto', 'oto'].forEach(category => {
          const stats = calculateStats(unitRecords, category);
          dataRow.getCell(dataColIndex).value = stats.total;
          dataRow.getCell(dataColIndex + 1).value = stats.eligible;
          dataRow.getCell(dataColIndex + 2).value = stats.dichVuCong; // Dữ liệu từ cột Dịch vụ công toàn trình
          dataRow.getCell(dataColIndex + 3).value = stats.ineligible;
          const ratioCell = dataRow.getCell(dataColIndex + 4);
          ratioCell.value = { formula: `IF(${dataRow.getCell(dataColIndex).address}>0, ${dataRow.getCell(dataColIndex + 1).address}/${dataRow.getCell(dataColIndex).address}, 0)` };
          ratioCell.numFmt = '0.00%';
          dataColIndex += 5;
        });

        columns.forEach(col => {
          const cellValue = row[col.key];
          dataRow.getCell(dataColIndex++).value = cellValue || 0;
        });

        dataRow.eachCell((cell, colNumber) => {
          cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
          if (colNumber === 1) {
            Object.assign(cell, {
              font: { name: 'Arial', size: 14, bold: true },
              alignment: { horizontal: 'left', vertical: 'middle' },
              fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
            });
          } else {
            cell.font = { name: 'Arial', size: 14 };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            if (index % 2 === 0) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
            } else {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
            }
            if (cell.value > 0 && typeof cell.value === 'number') {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E8' } };
              cell.font = { name: 'Arial', size: 14, color: { argb: 'FF2D5A2D' } };
            }
          }
        });
        dataRow.height = 38;
        currentRow++;
      });
    }

    // Tạo và xuất file
    const fileName = `bao-cao-tong-hop-${startDate}${startDate !== endDate ? '-den-' + endDate : ''}.xlsx`;
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(link.href);

    // Restore button state
    if (exportBtn) {
      exportBtn.disabled = false;
      exportBtn.innerHTML = '📊';
    }
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    Swal.fire({
      icon: 'error',
      title: 'Lỗi!',
      text: 'Lỗi khi xuất Excel: ' + error.message,
      confirmButtonColor: '#1e3a8a'
    });
    
    // Restore button state on error
    const exportBtn = document.querySelector('.export-excel-btn');
    if (exportBtn) {
      exportBtn.disabled = false;
      exportBtn.innerHTML = '📊';
    }
  }
};

  // Tạo tiêu đề hiển thị các địa điểm đã chọn
  const getSelectedUnitsDisplay = () => {
    if (selectedUnits.includes('all')) {
      return 'Tất cả địa điểm';
    }
    
    if (selectedUnits.length === 1) {
      const unit = reportData?.units?.find(u => u.id.toString() === selectedUnits[0]);
      return unit ? unit.name : '';
    }
    
    const unitNames = selectedUnits
      .map(id => reportData?.units?.find(u => u.id.toString() === id)?.name)
      .filter(name => name);
    
    return unitNames.join(', ');
  };

  return (
    <div className="spreadsheet-report">
      <div className="report-header">
        <div className="header-top">
          <h2>
            Báo cáo {isDaily ? 'chi tiết từng ngày' : 'tổng hợp'} {formatDateRange()}
          </h2>
          <button
            className="export-excel-btn"
            onClick={exportToExcel}
            disabled={!tableStructure}
            title="Xuất báo cáo Excel"
          >
            📊
          </button>
        </div>
        <div className="filters">
          <div className="date-filter">
            <label>Từ ngày:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="date-filter">
            <label>Đến ngày:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          {(user.role === 'admin' || user.role === 'supervisor') && (
            <div className="unit-filter">
              <label>Địa điểm:</label>
              <SearchableMultiSelect
                options={unitOptions}
                selectedValues={selectedUnits}
                onChange={handleUnitsChange}
                placeholder="Chọn địa điểm..."
                searchPlaceholder="Gõ để tìm kiếm địa điểm..."
              />
            </div>
          )}
          <div className="search-button-container">
            <label>&nbsp;</label>
            <button 
              className="search-btn" 
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? '🔄 Đang tải...' : '🔍 Tìm kiếm'}
            </button>
          </div>
        </div>
      </div>

      <div 
        className="spreadsheet-container"
        ref={containerRef}
        onDragOver={handleDragOverContainer}
      >
        <table className="spreadsheet-table">
          <thead>
            {/* Header cấp 1: Loại phương tiện */}
            <tr className="vehicle-header">
              <th rowSpan="2" className="unit-header">
                {isDaily ? (isDailyPerUnit ? 'Ngày - Địa điểm' : 'Ngày') : 'Địa điểm'}
              </th>
              {Object.values(groupedColumns).map(group => (
                <th key={group.vehicleType.id} colSpan={group.columns.length} className="vehicle-type-header">
                  {group.vehicleType.name}
                </th>
              ))}
            </tr>
            {/* Header cấp 2: Nghiệp vụ */}
            <tr className="business-header">
              {Object.values(groupedColumns).map(group =>
                group.columns.map(col => (
                  <th key={col.key} className="business-type-header">
                    {col.businessType.name}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {/* Dòng tổng */}
            <tr className="total-row">
              <td className="unit-name total-label">Tổng</td>
              {columns.map(col => {
                const totalValue = totals[col.key];
                return (
                  <td key={col.key} className="total-value">
                    {totalValue || 0}
                  </td>
                );
              })}
            </tr>
            
            {/* Dữ liệu từng đơn vị hoặc từng ngày */}
            {tableData.map((row, idx) => (
              row.isGroupHeader ? (
                <tr key={`group-${row.unit.id}-${idx}`} className="group-header-row">
                  <td className="unit-name group-label" colSpan={1 + columns.length}>{row.unit.name}</td>
                </tr>
              ) : (
                <tr 
                  key={isDaily ? `${row.originalDate}-${isDailyPerUnit ? row.unit.id : 'single'}` : row.unit.id} 
                  className={`data-row ${!row.hasData ? 'no-data-row' : ''} ${dragOverItemIndex === idx ? 'drag-over' : ''} ${draggedItemIndex === idx ? 'dragging' : ''}`}
                  draggable={!isDaily && (user.role === 'admin' || user.role === 'supervisor')}
                  onDragStart={(e) => !isDaily && handleDragStart(e, idx)}
                  onDragOver={(e) => !isDaily && handleDragOver(e, idx)}
                  onDrop={(e) => !isDaily && handleDrop(e, idx)}
                >
                  <td 
                    className="unit-name"
                    style={!isDaily && (user.role === 'admin' || user.role === 'supervisor') ? { cursor: 'grab' } : {}}
                    title={!isDaily && (user.role === 'admin' || user.role === 'supervisor') ? "Kéo thả để đổi thứ tự đơn vị" : ""}
                  >
                    {isDaily ? (isDailyPerUnit ? row.date : row.date) : row.unit.name}
                  </td>
                  {columns.map(col => {
                    const cellValue = row[col.key];
                    return (
                      <td key={col.key} className="data-value">
                        {cellValue || 0}
                      </td>
                    );
                  })}
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination controls */}
      {reportData?.pagination && reportData.pagination.totalPages > 1 && (
        <div className="pagination-info">
          <p>
            Đang hiển thị {allRecords.length} / {reportData.pagination.total} bản ghi
            {currentPage < reportData.pagination.totalPages && (
              <button 
                onClick={loadMoreRecords} 
                disabled={loadingMore}
                className="load-more-btn"
              >
                {loadingMore ? 'Đang tải...' : 'Tải thêm dữ liệu'}
              </button>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default SpreadsheetReport;
